// เหตุการณ์ของสัตว์อพยพ (src/data/migrants.ts, src/geo/migration.ts)
// {destination} คือแหล่งที่ฤดูนี้ควรไป {month} คือเดือนปัจจุบัน {migrationFact} คือข้อเท็จจริงของชนิดนั้น

import type { Choice, GameEvent } from "../../engine/types";

const DEPART: readonly Choice[] = [
  {
    label: "ออกเดินทางเลย",
    outcomes: [
      { chance: 0.7, text: "คุณเดินทางไกลหลายวันจนถึง{destination}", effects: { fullness: -25, move: "migrate" } },
      { chance: 0.2, text: "ระหว่างทางเจอพายุ คุณไปถึง{destination}ได้อย่างบอบช้ำ", effects: { fullness: -25, health: -25, move: "migrate", cause: "disaster" } },
      { chance: 0.1, text: "พลังงานที่สะสมไว้ไม่พอ คุณหมดแรงและต้องหยุดกลางทาง", effects: { fullness: -35, health: -20, cause: "starvation" } },
    ],
  },
  {
    label: "กินให้อ้วนก่อนแล้วค่อยไป",
    outcomes: [
      { chance: 0.8, text: "คุณกินสะสมพลังงานไว้เต็มที่ พร้อมสำหรับทางไกล", effects: { fullness: 25 } },
      { chance: 0.2, text: "อาหารแถวนี้หมดลงเร็วกว่าที่คิด เพราะเพื่อนๆ ต่างก็ออกเดินทางไปแล้ว", effects: { fullness: -5, safety: -10 } },
    ],
  },
];

// สกุลของปลาแซลมอน เหตุการณ์ใช้คู่กับ migration จึงเกิดเฉพาะชนิดที่อพยพจริง (src/data/migrants.ts)
const SALMON = ["Oncorhynchus", "Salmo"];

export const MIGRATION_EVENTS: readonly GameEvent[] = [
  {
    id: "migrate_depart",
    stageTags: ["young", "adult", "old"],
    requires: { migration: "due", sessile: false },
    weight: 40, // ใช้กับสัตว์ที่ turn ยาวกว่าฤดู (turn สั้นถูกบังคับให้เกิดเมื่อถึงฤดูอยู่แล้ว)
    text: "เดือน{month}มาถึง ร่างกายของคุณกระวนกระวายบอกว่าถึงเวลาเดินทางไป{destination}แล้ว",
    fact: "{migrationFact}",
    choices: DEPART,
  },
  {
    id: "migrate_first_journey",
    archetypes: ["BIRD"],
    stageTags: ["young"],
    requires: { migration: "due", sessile: false },
    weight: 40, // ใช้กับสัตว์ที่ turn ยาวกว่าฤดู (turn สั้นถูกบังคับให้เกิดเมื่อถึงฤดูอยู่แล้ว)
    text: "ตัวที่โตแล้วเริ่มออกเดินทางไป{destination} นี่จะเป็นการอพยพครั้งแรกในชีวิตของคุณ",
    fact: "นกอพยพหลายชนิด ลูกนกที่เพิ่งโตบินอพยพครั้งแรกโดยไม่มีพ่อแม่นำทาง และหาทางไปถึงแหล่งหลบหนาวได้ด้วยสัญชาตญาณ",
    choices: [
      {
        label: "ออกเดินทางไปกับฝูง",
        outcomes: [
          { chance: 0.75, text: "คุณตามฝูงไปจนถึง{destination}", effects: { fullness: -25, move: "migrate" } },
          { chance: 0.25, text: "คุณพลัดหลงจากฝูง แต่สุดท้ายก็หาทางไปถึง{destination}จนได้", effects: { fullness: -30, health: -15, move: "migrate", cause: "starvation" } },
        ],
      },
      {
        label: "รอให้ตัวแข็งแรงกว่านี้",
        outcomes: [{ chance: 1, text: "คุณกินสะสมพลังงานอีกสักพัก ขณะที่ฝูงทยอยออกเดินทาง", effects: { fullness: 20, safety: -5 } }],
      },
    ],
  },
  {
    id: "migrant_fattening",
    archetypes: ["BIRD"],
    stageTags: ["young", "adult", "old"],
    requires: { migration: "migrant" },
    weight: 3,
    text: "ใกล้ถึงฤดูเดินทาง คุณหิวผิดปกติ กินเท่าไรก็ไม่อิ่ม",
    fact: "ก่อนออกเดินทาง นกอพยพบางชนิดกินจนน้ำหนักตัวเพิ่มเกือบเท่าตัว นกปากแอ่นหางลายยังหดอวัยวะย่อยอาหารให้เล็กลงเพื่อให้ตัวเบาตอนบินไกล",
    choices: [
      { label: "กินให้มากที่สุด", outcomes: [{ chance: 1, text: "คุณสะสมไขมันไว้เป็นเชื้อเพลิงสำหรับทางไกล", effects: { fullness: 35 } }] },
      {
        label: "กินพอดีๆ จะได้บินคล่อง",
        outcomes: [
          { chance: 0.6, text: "ตัวเบาทำให้คุณหลบนักล่าได้ง่าย", effects: { fullness: 15, safety: 10 } },
          { chance: 0.4, text: "คุณกังวลว่าพลังงานจะไม่พอสำหรับทางไกล", effects: { fullness: 15 } },
        ],
      },
    ],
  },
  {
    id: "migrant_city_lights",
    archetypes: ["BIRD"],
    stageTags: ["young", "adult", "old"],
    requires: { migration: "migrant", canFly: true },
    weight: 2,
    text: "คืนนี้คุณบินผ่านเมืองใหญ่ แสงไฟสว่างจ้าจนมองไม่เห็นดาว",
    fact: "นกอพยพหลายชนิดบินกลางคืนและใช้ดวงดาวกับสนามแม่เหล็กโลกนำทาง แสงไฟจากเมืองทำให้นกหลงทางและบินชนอาคารเป็นจำนวนมากทุกปี",
    choices: [
      {
        label: "บินต่อผ่านเมืองไป",
        outcomes: [
          { chance: 0.7, text: "คุณบินพ้นแสงไฟไปได้ และหาทางกลับมาตามดวงดาวได้อีกครั้ง", effects: { fullness: -10 } },
          { chance: 0.3, text: "คุณบินวนอยู่รอบตึกสูงที่เปิดไฟสว่างจนเกือบชนกระจก", effects: { health: -25, cause: "human" } },
        ],
      },
      { label: "ลงพักในสวนสาธารณะก่อน", outcomes: [{ chance: 1, text: "คุณพักจนรุ่งเช้า แล้วบินต่อตอนที่มองเห็นทางชัด", effects: { fullness: -5, safety: 5 } }] },
    ],
  },
  {
    id: "butterfly_depart",
    stageTags: ["adult"],
    requires: { migration: "due", subgroups: ["insect.butterfly"] },
    weight: 40,
    text: "ดอกไม้แถวนี้เริ่มโรย กลางวันสั้นลง ผีเสื้อชนิดเดียวกับคุณเริ่มบินไปทางเดียวกันหมด ราวกับรู้ว่าต้องไปที่{destination}",
    fact: "{migrationFact}",
    choices: [
      {
        label: "บินขึ้นสูงไปกับลมหนุน",
        outcomes: [
          { chance: 0.75, text: "คุณร่อนไปกับลมที่ความสูงหลายร้อยเมตร ไปได้วันละหลายร้อยกิโลเมตรจนถึง{destination}", effects: { fullness: -25, move: "migrate" } },
          { chance: 0.25, text: "พายุฝนพัดฝูงผีเสื้อกระจัดกระจาย ปีกของคุณขาดไปนิดหนึ่ง แต่ก็ยังไปถึง{destination}", effects: { fullness: -30, health: -30, move: "migrate", cause: "disaster" } },
        ],
      },
      {
        label: "บินต่ำๆ แวะดูดน้ำหวานไปตลอดทาง",
        outcomes: [
          { chance: 0.6, text: "คุณเติมพลังงานจากดอกไม้ริมทางได้เรื่อยๆ ช้าหน่อยแต่ก็ถึง{destination}", effects: { fullness: -10, move: "migrate" } },
          { chance: 0.4, text: "นกตัวหนึ่งโฉบเข้ามาตอนคุณเกาะดอกไม้ คุณหลบได้แต่ปีกฉีก กว่าจะบินถึง{destination}ก็อ่อนแรง", effects: { fullness: -15, health: -30, move: "migrate", cause: "predator" } },
        ],
      },
      { label: "อยู่ดูดน้ำหวานที่นี่อีกสักพัก", outcomes: [{ chance: 1, text: "คุณได้น้ำหวานอีกนิด แต่อากาศเริ่มไม่เป็นใจ", effects: { fullness: 15, safety: -5 } }] },
    ],
  },
  {
    id: "whale_depart",
    stageTags: ["young", "adult", "old"],
    requires: { migration: "due", subgroups: ["mammal.whale"] },
    weight: 40,
    text: "วาฬตัวอื่นรอบตัวคุณเริ่มทยอยออกเดินทางไปยัง{destination} เสียงร้องของพวกมันดังก้องไปไกลหลายกิโลเมตรใต้น้ำ",
    fact: "{migrationFact}",
    choices: [
      {
        label: "ว่ายตามกลุ่มไปตามเส้นทางเดิมทุกปี",
        outcomes: [
          { chance: 0.7, text: "คุณว่ายติดต่อกันหลายสัปดาห์ หายใจเป็นจังหวะ จนถึง{destination}", effects: { fullness: -25, move: "migrate" } },
          { chance: 0.15, text: "เส้นทางตัดผ่านเส้นทางเดินเรือ เรือสินค้าลำใหญ่แล่นผ่านเฉียดหลังคุณ คุณได้แผลก่อนถึง{destination}", effects: { fullness: -25, health: -35, move: "migrate", cause: "human" } },
          { chance: 0.15, text: "ครีบของคุณไปพันกับเชือกทุ่นของลอบปู กว่าจะสะบัดหลุดก็เหนื่อยแทบหมดแรง แต่ก็ไปถึง{destination}", effects: { fullness: -35, health: -25, move: "migrate", cause: "human" } },
        ],
      },
      {
        label: "กินให้อิ่มอีกสักพักแล้วค่อยตามไป",
        outcomes: [
          { chance: 0.7, text: "คุณสะสมไขมันใต้ผิวหนังไว้หนาขึ้น พร้อมสำหรับการเดินทางที่แทบไม่ได้กินอะไรเลย", effects: { fullness: 25 } },
          { chance: 0.3, text: "อาหารแถวนี้เริ่มหมด และคุณกลายเป็นวาฬไม่กี่ตัวที่ยังอยู่ ฝูงวาฬเพชฌฆาตเริ่มว่ายวนเข้ามา", effects: { fullness: 10, safety: -20 } },
        ],
      },
    ],
  },
  {
    id: "whale_calf_journey",
    stageTags: ["dependent", "young"],
    requires: { migration: "migrant", subgroups: ["mammal.whale"] },
    weight: 4,
    text: "คุณว่ายชิดข้างแม่ตลอดทาง ไกลออกไปมีครีบหลังสูงตั้งชันหลายครีบกำลังเข้ามาใกล้",
    fact: "วาฬเพชฌฆาตบางฝูงดักรอลูกวาฬสีเทาและลูกวาฬหลังค่อมตามเส้นทางอพยพ แม่วาฬจึงมักพาลูกว่ายเลียบชายฝั่งน้ำตื้น",
    choices: [
      {
        label: "ว่ายหลบเข้าน้ำตื้นตามแม่",
        outcomes: [
          { chance: 0.75, text: "ฝูงวาฬเพชฌฆาตเลิกตามเมื่อคุณเข้าใกล้ฝั่ง", effects: { fullness: -10, safety: 5 } },
          { chance: 0.25, text: "วาฬเพชฌฆาตตามมาทัน แม่วาฬปกป้องคุณไว้ได้ แต่คุณได้แผล", effects: { health: -30, cause: "predator" } },
        ],
      },
      {
        label: "ซุกตัวใต้ท้องแม่",
        outcomes: [
          { chance: 0.6, text: "แม่หันหางฟาดน้ำไล่ จนฝูงวาฬเพชฌฆาตถอยไป", effects: { safety: 10 } },
          { chance: 0.4, text: "การไล่ล่ากินเวลาหลายชั่วโมง คุณหมดแรงไปมาก", effects: { fullness: -20, health: -15, cause: "predator" } },
        ],
      },
    ],
  },
  {
    id: "mekong_spawning_run",
    stageTags: ["adult"],
    requires: { migration: "migrant", families: ["Pangasiidae"] },
    weight: 4,
    threatenedWeight: 1.5,
    text: "คุณว่ายทวนแม่น้ำโขงขึ้นเหนือ ข้างหน้ามีกำแพงคอนกรีตขวางลำน้ำไว้",
    fact: "ปลาบึกเป็นสัตว์ใกล้สูญพันธุ์อย่างยิ่ง เขื่อนที่ขวางลำน้ำและการจับปลาทำให้ปลาที่ว่ายขึ้นไปวางไข่ทางตอนเหนือลดลงมาก",
    choices: [
      {
        label: "หาทางอ้อมผ่านไป",
        outcomes: [
          { chance: 0.5, text: "คุณเจอร่องน้ำด้านข้างที่พอผ่านไปได้", effects: { fullness: -20 } },
          { chance: 0.5, text: "ไม่มีทางผ่าน คุณว่ายวนอยู่หน้ากำแพงจนหมดแรง", effects: { fullness: -25, health: -20, cause: "human" } },
        ],
      },
      { label: "วางไข่ในแม่น้ำช่วงล่างแทน", outcomes: [{ chance: 1, text: "ที่นี่ไม่ใช่แหล่งวางไข่ที่ดีนัก แต่ก็ดีกว่าไม่ได้วางเลย", effects: { fullness: -10, safety: 5 } }] },
    ],
  },

  // ---------- ปลาแซลมอน: เกิดในลำธาร ลงทะเล แล้วกลับมาวางไข่ที่ลำธารเดิม (src/geo/migration.ts anadromousGround) ----------
  {
    id: "salmon_parr",
    stages: ["larva", "juvenile"],
    requires: { migration: "at_a", genera: SALMON },
    weight: 6,
    text: "คุณเป็นปลาตัวเล็กลายจุดในลำธารน้ำเย็น ปลาตัวอื่นก็แย่งกันจับตัวอ่อนแมลงที่ลอยมาตามน้ำ",
    fact: "ลูกปลาแซลมอนอยู่ในลำธารหรือทะเลสาบน้ำจืดราว 1–3 ปีก่อนลงทะเล ลายจุดข้างลำตัวช่วยพรางตัวกับพื้นกรวด",
    choices: [
      {
        label: "ยึดแอ่งน้ำวนหลังก้อนหินไว้",
        outcomes: [
          { chance: 0.7, text: "คุณไล่ตัวอื่นออกไป แล้วดักกินแมลงที่ไหลผ่านมาได้สบายๆ", effects: { fullness: 20 } },
          { chance: 0.3, text: "นกกระเต็นโฉบลงมาตรงแอ่งที่คุณอยู่พอดี", effects: { health: -25, cause: "predator" } },
        ],
      },
      { label: "ซ่อนตัวใต้รากไม้ริมตลิ่ง", outcomes: [{ chance: 1, text: "คุณปลอดภัย แต่อาหารที่ลอยมาถึงตรงนี้มีไม่มาก", effects: { fullness: 5, safety: 10 } }] },
    ],
  },
  {
    id: "salmon_smolt_run",
    stageTags: ["adult"],
    requires: { migration: "due_b", genera: SALMON },
    weight: 40,
    text: "ลายจุดของคุณจางลงจนลำตัวเป็นสีเงินวาว น้ำจืดเริ่มทำให้รู้สึกแปลกๆ ถึงเวลาว่ายตามน้ำลงไปยัง{destination}แล้ว",
    fact: "ก่อนลงทะเล ลูกปลาแซลมอนเปลี่ยนร่างกายให้อยู่ในน้ำเค็มได้ ลำตัวกลายเป็นสีเงินเพื่อพรางตัวในทะเลเปิด และจำกลิ่นของลำธารไว้ใช้หาทางกลับบ้าน",
    choices: [
      {
        label: "ว่ายตามกระแสน้ำลงทะเลตอนกลางคืน",
        outcomes: [
          { chance: 0.75, text: "คุณไหลตามน้ำไปกับฝูงนับพันตัวจนถึงปากแม่น้ำ แล้วออกสู่{destination}", effects: { fullness: -15, move: "migrate" } },
          { chance: 0.25, text: "นกนางนวลและปลาตัวใหญ่ดักรออยู่ที่ปากแม่น้ำ คุณหลุดรอดออกทะเลมาได้แต่ได้แผล", effects: { fullness: -15, health: -25, move: "migrate", cause: "predator" } },
        ],
      },
      { label: "รออยู่ในแม่น้ำอีกสักพัก", outcomes: [{ chance: 1, text: "ลำธารเริ่มคับแคบสำหรับคุณ อาหารก็ไม่พอให้ตัวโตขึ้นอีกแล้ว", effects: { fullness: 5, safety: -5 } }] },
    ],
  },
  {
    id: "salmon_ocean",
    stageTags: ["adult"],
    requires: { migration: "at_b", genera: SALMON },
    weight: 8,
    text: "คุณว่ายตามฝูงปลาเล็กและกุ้งเคยในทะเลเปิด กินจนตัวโตขึ้นทุกวัน แต่วันนี้มีเงาดำใหญ่ว่ายวนอยู่ข้างล่าง",
    fact: "ปลาแซลมอนโตเร็วมากในทะเลเพราะอาหารอุดมสมบูรณ์ ปลาแซลมอนชินูกเป็นอาหารหลักของวาฬเพชฌฆาตบางฝูงนอกชายฝั่งอเมริกาเหนือ",
    choices: [
      {
        label: "ว่ายเข้ากลางฝูง",
        outcomes: [
          { chance: 0.7, text: "เงานั้นคว้าตัวอื่นไปแทน คุณกินต่อจนอิ่ม", effects: { fullness: 25, safety: -5 } },
          { chance: 0.3, text: "วาฬเพชฌฆาตพุ่งเข้ากลางฝูง คุณหลบได้หวุดหวิดแต่เกล็ดหลุดไปทั้งแถบ", effects: { fullness: 10, health: -30, cause: "predator" } },
        ],
      },
      { label: "ว่ายหนีลงไปในน้ำลึก", outcomes: [{ chance: 1, text: "คุณปลอดภัย แต่ในน้ำลึกอาหารน้อยกว่ามาก", effects: { fullness: -5, safety: 10 } }] },
    ],
  },
  {
    id: "salmon_homing",
    stageTags: ["adult", "old"],
    requires: { migration: "due_a", genera: SALMON },
    weight: 40,
    text: "กลางทะเลกว้าง คุณได้กลิ่นจางๆ ที่จำได้ตั้งแต่ยังเล็ก กลิ่นของลำธารที่คุณเกิด ถึงเวลากลับไป{destination}แล้ว",
    fact: "ปลาแซลมอนใช้สนามแม่เหล็กโลกนำทางกลับมาถึงชายฝั่ง แล้วใช้กลิ่นของน้ำหาลำธารสายที่ตัวเองเกิด ระหว่างว่ายทวนน้ำกลับบ้านแทบไม่กินอะไรเลย",
    choices: [
      {
        label: "ว่ายทวนน้ำกลับบ้าน",
        outcomes: [
          { chance: 0.55, text: "คุณกระโดดข้ามแก่งหินและน้ำตกทีละขั้นจนถึงลำธารที่คุณเกิด", effects: { fullness: -30, move: "migrate" } },
          { chance: 0.25, text: "หมีสีน้ำตาลยืนดักรออยู่บนน้ำตก กรงเล็บของมันเฉี่ยวคุณไปหนึ่งแผลก่อนที่คุณจะกระโดดพ้นขึ้นไป", effects: { fullness: -30, health: -30, move: "migrate", cause: "predator" } },
          { chance: 0.2, text: "อวนของชาวประมงขึงขวางปากแม่น้ำอยู่ คุณติดอวนจนครีบฉีก กว่าจะดิ้นหลุดออกมาได้", effects: { fullness: -15, health: -40, cause: "human" } },
        ],
      },
      { label: "หากินในทะเลอีกสักพัก", outcomes: [{ chance: 1, text: "คุณกินสะสมไขมันไว้อีก ตัวใหญ่ขึ้นก็มีพลังว่ายทวนน้ำมากขึ้น", effects: { fullness: 25, safety: -5 } }] },
    ],
  },
  {
    id: "salmon_spawn",
    stageTags: ["adult", "old"],
    requires: { migration: "at_a", genera: SALMON, notFlags: ["spent", "kelt"] },
    weight: 60,
    text: "ลำตัวของคุณเปลี่ยนเป็นสีแดงเข้ม ปากงุ้มเป็นตะขอ พื้นกรวดในลำธารที่คุณเกิดเต็มไปด้วยปลาที่กลับมาวางไข่เหมือนกัน",
    fact: "ปลาแซลมอนตัวเมียใช้หางปัดกรวดเป็นแอ่งวางไข่ ส่วนตัวผู้ต่อสู้กันแย่งตัวเมีย ซากปลาที่ตายหลังวางไข่เป็นอาหารของหมี นกอินทรี และปุ๋ยของป่าริมลำธาร",
    choices: [
      { label: "ปัดกรวดทำแอ่งแล้ววางไข่", outcomes: [{ chance: 1, text: "คุณวางไข่ {n} ฟองลงในแอ่งกรวด แล้วกลบไว้อย่างดี", effects: { offspring: "clutch", addFlags: ["parent", "spent"], fullness: -20 } }] },
      {
        label: "สู้แย่งตำแหน่งวางไข่ที่ดีที่สุด",
        outcomes: [
          { chance: 0.6, text: "คุณชนะ ได้วางไข่ {n} ฟองตรงที่น้ำไหลผ่านกรวดดีที่สุด", effects: { offspring: "clutch", addFlags: ["parent", "spent"], fullness: -25, health: -10 } },
          { chance: 0.4, text: "คุณได้แผลจากการต่อสู้ แต่ก็หาที่วางไข่ {n} ฟองได้ในที่สุด", effects: { offspring: "clutch", addFlags: ["parent", "spent"], fullness: -25, health: -25, cause: "accident" } },
        ],
      },
    ],
  },
  {
    id: "salmon_final",
    stageTags: ["adult", "old"],
    requires: { migration: "migrant", genera: ["Oncorhynchus"], flags: ["spent"] },
    weight: 500,
    text: "หลังวางไข่ ร่างกายของคุณใช้พลังงานจนหมด เกล็ดหลุดลอก คุณลอยอยู่ข้างแอ่งกรวดที่ฝังไข่ไว้",
    fact: "ปลาแซลมอนแปซิฟิกตายหลังวางไข่ครั้งเดียว ธาตุอาหารจากทะเลในซากปลาช่วยให้ต้นไม้ริมลำธารโตได้ดีกว่าที่อื่น",
    choices: [
      { label: "เฝ้าแอ่งไข่จนถึงวินาทีสุดท้าย", outcomes: [{ chance: 1, text: "คุณไล่ปลาตัวอื่นที่จะมาขุดรังจนหมดแรง แล้วนอนนิ่งอยู่บนกรวดข้างไข่ของคุณ", effects: { death: "sacrifice" } }] },
      { label: "ปล่อยตัวลอยไปตามน้ำ", outcomes: [{ chance: 1, text: "คุณลอยไปตามกระแสน้ำ ร่างของคุณจะกลายเป็นอาหารให้ลำธารที่ลูกๆ กำลังจะเกิด", effects: { death: "old_age" } }] },
    ],
  },
  {
    id: "salmon_kelt",
    stageTags: ["adult", "old"],
    requires: { migration: "migrant", genera: ["Salmo"], flags: ["spent"] },
    weight: 500,
    text: "หลังวางไข่ คุณผอมลงจนเหลือแต่หนัง แต่หัวใจยังเต้นอยู่",
    fact: "ปลาแซลมอนแอตแลนติกส่วนใหญ่ตายหลังวางไข่ แต่ราว 1 ใน 10 รอดชีวิต เรียกว่า kelt และบางตัวกลับมาวางไข่ได้อีกในปีต่อๆ ไป",
    choices: [
      {
        label: "พยุงตัวให้รอด",
        outcomes: [
          { chance: 0.25, text: "คุณรอดมาได้ ค่อยๆ ฟื้นแรงอยู่ในแอ่งน้ำลึกของแม่น้ำ", effects: { removeFlags: ["spent"], addFlags: ["kelt"], health: -20, fullness: 10 } },
          { chance: 0.75, text: "ร่างกายของคุณไม่เหลือแรงพอ คุณจากไปอย่างสงบในแม่น้ำที่คุณเกิด", effects: { death: "old_age" } },
        ],
      },
    ],
  },
];
